import { DRAW_FIT_CAMERA_AFTER_DRAW } from '@constants'
import { AnyID, Getter, SceneObjects, Setter } from '@types'
import { applyLiveDrawCameraFit } from '@utils/camera/cameraControlsRegistry'
import { useModularUiStore } from '@utils/modularSnap/modularUiStore'
import { childBaseCatalogFromUnit } from '@utils/atemporal/seatHeightCatalog'
import { enforceSeatCushionLocalCoords } from '@utils/atemporalSeatCushionLocalCoords'
import { runWithoutHistoryRecordingAsync } from '@utils/historyRecording'
import { realignExteriorArmsAfterUnitDraw } from '@utils/linkedArms/realignExteriorArmsAfterUnitDraw'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import getId from '@utils/sceneObjects/getId'
import { isPouff, isRinconeroCatalog, isModularCornerSnapCatalog, isOreja } from '@utils/sceneObjects/items/rinconeroUtils'
import { drawSnapLog } from './drawSnapLog'
import { preloadSceneTextureUrls } from '@utils/sceneObjects/textureCache'
import { Matrix4 } from 'three'
import type { DrawItemOptions } from './itemActions'

/**
 * Contexto compartido por todos los comportamientos post-dibujo de `drawItem`.
 * Reúne el estado calculado durante el dibujo para completar el comportamiento
 * específico de cada tipo de item una vez ya colocado en la escena.
 */
export type DrawItemPostProcessingContext = {
  get: Getter
  set: Setter
  item: any
  catalogID: string
  type: SceneObjects['type']
  backGLBInfo: any
  options?: DrawItemOptions
  pendingParentSnap: { anchorId: AnyID; snapSide: 'left' | 'right' } | null
  sourceCurrentObjectID: AnyID | ''
  preserveWorldTransform: boolean
  suppressAutoSeatAndCushion: boolean
  suppressCameraFit: boolean
  suppressExteriorArmRealign: boolean
}

/**
 * Reaplica los materiales de zona de un linkedItem al renderer sin disparar
 * múltiples incrementos del contador global (evita parpadeo negro en el visor).
 */
const pushLinkedItemZoneMaterialsToRenderer = (get: Getter, itemId: AnyID) => {
  const liveItem = get().items.get(itemId) as any
  if (!liveItem?.textures?.length) return
  liveItem.textures.forEach((tex: { zoneName?: string }) => {
    const zoneName = tex.zoneName
    const material = liveItem.zones?.[zoneName]?.material
    if (!zoneName || !material) return
    // skipCounterIncrement: evita N incrementos globales de contadorModificaciones
    // (provocan parpadeo negro en TODAS las unidades linkedItem del visor).
    get().editZone(
      itemId,
      zoneName,
      {
        ...material,
        color:
          typeof material.color === 'string' && material.color.trim().length > 0
            ? material.color
            : '#FFFFFF',
        url: material.url ? String(material.url).split('?')[0] : material.url,
      },
      true,
    )
  })
}

/**
 * linkedItem: reaplica el snap con la misma lógica usada en `collideItems`
 * para que la colocación final de `drawItem` coincida con ese flujo.
 */
const reapplyParentSnapAfterDraw = (ctx: DrawItemPostProcessingContext) => {
  const { get, set, item, pendingParentSnap, preserveWorldTransform, options, catalogID } = ctx
  if (
    pendingParentSnap &&
    !item.universal &&
    item.type === 'linkedItem' &&
    !preserveWorldTransform &&
    !options?.useChainedDrawMatrix
  ) {
    const anchor = get().items.get(pendingParentSnap.anchorId as AnyID) as any
    const anchorCatalog = String(anchor?.catalogID ?? '').toLowerCase()
    const drawingCatalog = String(catalogID ?? '').toLowerCase()
    const isStraightOnRinconero =
      anchor &&
      isRinconeroCatalog(anchorCatalog) &&
      !isModularCornerSnapCatalog(drawingCatalog) &&
      !isOreja(drawingCatalog)

    if (isStraightOnRinconero) {
      drawSnapLog('post-draw:omitir-collideItems-rinconero-pata', {
        itemId: String(item.id),
        anchorId: String(pendingParentSnap.anchorId),
        drawingCatalog,
        anchorCatalog,
      })
      return
    }

    try {
      drawSnapLog('post-draw:collideItems', {
        itemId: String(item.id),
        anchorId: String(pendingParentSnap.anchorId),
        snapSide: pendingParentSnap.snapSide,
        drawingCatalog,
      })
      get().collideItems(item.id as AnyID, pendingParentSnap.anchorId as AnyID, {
        forcedSide: pendingParentSnap.snapSide,
      })

      // En frameloop=demand, forzar un repaint tras snap post-dibujo.
      set((s) => {
        s.contadorModificaciones = (s.contadorModificaciones || 0) + 1
      })
      try {
        const r3f = typeof window !== 'undefined' ? (window as any).r3f : undefined
        if (r3f?.invalidate) {
          r3f.invalidate()
          requestAnimationFrame(() => r3f.invalidate?.())
          setTimeout(() => r3f.invalidate?.(), 40)
        }
      } catch {
        // noop
      }
    } catch (postSnapError) {
      console.warn('[drawItem] No se pudo aplicar post-snap OGadapted:', postSnapError)
    }
  }
}

/**
 * linkedItem (unidad): auto-dibuja asiento y cojines del padre cuando faltan,
 * deduplicando y normalizando coordenadas locales de los hijos.
 * Debe completarse dentro del batch de drawItem (sin setTimeout suelto).
 */
const runAutoSeatAndCushion = async (ctx: DrawItemPostProcessingContext): Promise<void> => {
  const { get, set, item, catalogID, type, suppressAutoSeatAndCushion } = ctx
  const shouldAutoDrawSeat =
    !suppressAutoSeatAndCushion &&
    !item.universal &&
    item.type === 'linkedItem' &&
    type !== 'linkedSon' &&
    !/^BR/i.test(String(catalogID)) &&
    !/^ASIENTO/i.test(String(catalogID))

  if (!shouldAutoDrawSeat) return

  // Esperar a que termine el flujo normal de hijos del backend.
  await new Promise<void>((resolve) => {
    setTimeout(resolve, 40)
  })

  const storeNow = get()
  const liveParent = storeNow.items.get(item.id as AnyID) as any
  if (!liveParent || liveParent.type !== 'linkedItem' || !liveParent.matrix) return

      const parentCatalog = childBaseCatalogFromUnit(
        String(liveParent.catalogID || catalogID || '').trim(),
      )
      if (!parentCatalog) return

      const parentChildIds = Array.from((liveParent?.childs as Set<AnyID>) || [])
      const inheritedSeatFromParent = parentChildIds
        .map((childId) => get().items.get(childId as AnyID) as any)
        .find((child) => {
          if (child?.type !== 'linkedSon') return false
          const ref = String(child?.referencia || child?.catalogID || '').toLowerCase()
          return ref.includes('asiento')
        })
      const inheritedCushionFromParent = parentChildIds
        .map((childId) => get().items.get(childId as AnyID) as any)
        .find((child) => {
          if (child?.type !== 'linkedSon') return false
          const ref = String(child?.referencia || child?.catalogID || '').toLowerCase()
          return ref.includes('cojin')
        })

      const globalSeatStitchPref =
        typeof window !== 'undefined'
          ? String(window.localStorage.getItem('atemporal:autoGroup:seatStitchType') || '')
              .trim()
              .toLowerCase()
          : ''
      const globalCushionPref =
        typeof window !== 'undefined'
          ? String(window.localStorage.getItem('atemporal:autoGroup:cushionCatalog') || '')
              .trim()
              .toLowerCase()
          : ''

      const inheritedSeatCatalog = String(
        inheritedSeatFromParent?.catalogID || inheritedSeatFromParent?.referencia || '',
      ).toLowerCase()
      const inheritedCushionCatalog = String(
        inheritedCushionFromParent?.catalogID || inheritedCushionFromParent?.referencia || '',
      ).toLowerCase()
      const shouldUseSeatSin =
        inheritedSeatCatalog.includes('asientosin') || globalSeatStitchPref === 'sin'
      const shouldUseRectCushion =
        inheritedCushionCatalog.includes('cojinesr') || globalCushionPref === 'cojinesr'
      const seatBaseForSin = /^6-/i.test(parentCatalog)
        ? parentCatalog.replace(/^6-/i, '1-')
        : parentCatalog
      const targetSeatCatalog = shouldUseSeatSin
        ? `asientosin ${seatBaseForSin}`
        : `asiento ${parentCatalog}`
      const resolveCushionBaseFromSeatOrParent = () => {
        const seatsForParent = Array.from(get().items.values()).filter((sceneItem: any) => {
          if (sceneItem?.type !== 'linkedSon') return false
          if (String(sceneItem?.firstParentID || '') !== String(liveParent.id)) return false
          const ref = String(sceneItem?.referencia || sceneItem?.catalogID || '').toLowerCase()
          return ref.includes('asiento')
        }) as any[]
        const seatRef = String(
          seatsForParent[0]?.catalogID || seatsForParent[0]?.referencia || targetSeatCatalog,
        )
          .trim()
          .toLowerCase()
        const fromSeat = seatRef
          .replace(/^asientosin\s+/i, '')
          .replace(/^asiento\s+/i, '')
          .trim()
        return fromSeat || parentCatalog
      }
      const getTargetCushionCatalog = () =>
        `${shouldUseRectCushion ? 'cojinesR' : 'cojines'} ${resolveCushionBaseFromSeatOrParent()}`
      const isMatchingCushionForTarget = (child: any) => {
        const normalize = (v: any) =>
          String(v || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '')
        const expected = normalize(getTargetCushionCatalog())
        const actual = normalize(child?.catalogID || child?.referencia || '')
        return Boolean(
          expected && actual && (actual.includes(expected) || expected.includes(actual)),
        )
      }
      const parentIdStr = String(liveParent.id)
      const parentHasSeatInChilds = () => {
        const parentNow = get().items.get(liveParent.id as AnyID) as any
        const childIds = Array.from((parentNow?.childs as Set<AnyID>) || [])
        return childIds.some((childId) => {
          const child: any = get().items.get(childId as AnyID)
          if (child?.type !== 'linkedSon') return false
          const ref = String(child?.referencia || child?.catalogID || '').toLowerCase()
          return ref.includes('asiento')
        })
      }
      const parentHasCushionInChilds = () => {
        const parentNow = get().items.get(liveParent.id as AnyID) as any
        const childIds = Array.from((parentNow?.childs as Set<AnyID>) || [])
        return childIds.some((childId) => {
          const child: any = get().items.get(childId as AnyID)
          if (child?.type !== 'linkedSon') return false
          const ref = String(child?.referencia || child?.catalogID || '').toLowerCase()
          return ref.includes('cojin')
        })
      }
      const findSeatsForParent = () =>
        Array.from(get().items.entries()).filter(([, sceneItem]: any) => {
          if (sceneItem?.type !== 'linkedSon') return false
          if (String(sceneItem?.firstParentID || '') !== parentIdStr) return false
          const ref = String(sceneItem?.referencia || sceneItem?.catalogID || '').toLowerCase()
          return ref.includes('asiento')
        }) as Array<[AnyID, any]>
      const findCushionsForParent = () =>
        Array.from(get().items.entries()).filter(([, sceneItem]: any) => {
          if (sceneItem?.type !== 'linkedSon') return false
          if (String(sceneItem?.firstParentID || '') !== parentIdStr) return false
          const ref = String(sceneItem?.referencia || sceneItem?.catalogID || '').toLowerCase()
          return ref.includes('cojin')
        }) as Array<[AnyID, any]>
      const resetChildToParentLocalOrigin = (childId: AnyID, parentId: AnyID) => {
        set((state) => {
          const childItem = state.items.get(childId as AnyID) as any
          const parentItem = state.items.get(parentId as AnyID) as any
          if (!childItem || !parentItem) return
          enforceSeatCushionLocalCoords(childItem, parentItem)
        })
      }
      const normalizeSeatsAndCushionsForParent = () => {
        const seatsNow = findSeatsForParent()
        const cushionsNow = findCushionsForParent()
        seatsNow.forEach(([seatId]) =>
          resetChildToParentLocalOrigin(seatId, liveParent.id as AnyID),
        )
        cushionsNow.forEach(([cushionId]) =>
          resetChildToParentLocalOrigin(cushionId, liveParent.id as AnyID),
        )
      }

      const dedupeSeatsForParent = () => {
        const seats = findSeatsForParent()
        if (seats.length <= 1) return seats.length
        // Preferir conservar el asiento bloqueado (normalmente el "bueno" de catálogo).
        const sorted = [...seats].sort(
          (a, b) => Number(Boolean(b[1]?.isBlocked)) - Number(Boolean(a[1]?.isBlocked)),
        )
        const [keep, ...duplicates] = sorted
        set((state) => {
          duplicates.forEach(([dupId]) => {
            state.items.delete(dupId as AnyID)
            state.layers[state.currentLayer].items.delete(dupId as AnyID)
            state.layers[state.currentLayer].selected.delete(dupId as AnyID)
            state.sceneBoundingBoxes.delete(dupId as AnyID)
            state.collisionableItems.delete(dupId as AnyID)
            const parentItem = state.items.get(liveParent.id as AnyID) as any
            if (parentItem?.childs) {
              parentItem.childs.delete(dupId as AnyID)
            }
          })
          const parentItem = state.items.get(liveParent.id as AnyID) as any
          if (parentItem?.childs) {
            parentItem.childs.add(keep[0] as AnyID)
          }
        })
        return 1
      }
      const dedupeCushionsForParent = () => {
        const cushions = findCushionsForParent()
        if (cushions.length <= 1) return cushions.length
        const sorted = [...cushions].sort((a, b) => {
          const aMatches = isMatchingCushionForTarget(a[1]) ? 1 : 0
          const bMatches = isMatchingCushionForTarget(b[1]) ? 1 : 0
          if (bMatches !== aMatches) return bMatches - aMatches
          const aBlocked = Number(Boolean(a[1]?.isBlocked))
          const bBlocked = Number(Boolean(b[1]?.isBlocked))
          return bBlocked - aBlocked
        })
        const [keep, ...duplicates] = sorted
        set((state) => {
          duplicates.forEach(([dupId]) => {
            state.items.delete(dupId as AnyID)
            state.layers[state.currentLayer].items.delete(dupId as AnyID)
            state.layers[state.currentLayer].selected.delete(dupId as AnyID)
            state.sceneBoundingBoxes.delete(dupId as AnyID)
            state.collisionableItems.delete(dupId as AnyID)
            const parentItem = state.items.get(liveParent.id as AnyID) as any
            if (parentItem?.childs) {
              parentItem.childs.delete(dupId as AnyID)
            }
          })
          const parentItem = state.items.get(liveParent.id as AnyID) as any
          if (parentItem?.childs) {
            parentItem.childs.add(keep[0] as AnyID)
          }
        })
        return 1
      }
      const enforceMatchingCushionForParent = async () => {
        const cushions = findCushionsForParent()
        if (cushions.length === 0) return
        const hasMatching = cushions.some(([, child]) => isMatchingCushionForTarget(child))
        if (hasMatching) return
        set((state) => {
          cushions.forEach(([childId]) => {
            state.items.delete(childId as AnyID)
            state.layers[state.currentLayer].items.delete(childId as AnyID)
            state.layers[state.currentLayer].selected.delete(childId as AnyID)
            state.sceneBoundingBoxes.delete(childId as AnyID)
            state.collisionableItems.delete(childId as AnyID)
            const parentItem = state.items.get(liveParent.id as AnyID) as any
            if (parentItem?.childs) parentItem.childs.delete(childId as AnyID)
          })
        })
        try {
          const fixedCushionId = await get().drawItem(
            getId('linkedSon'),
            getTargetCushionCatalog(),
            '',
            true,
            liveParent.matrix.clone(),
            'linkedSon',
            parentCatalog,
            false,
            { preserveSelection: true },
          )
          set((state) => {
            const cushionItem = state.items.get(fixedCushionId as AnyID) as any
            const parentItem = state.items.get(liveParent.id as AnyID) as any
            if (!cushionItem || !parentItem) return
            cushionItem.firstParentID = liveParent.id
            cushionItem.depth = 1
            cushionItem.canCollide = false
            cushionItem.x = 0
            cushionItem.y = 0
            cushionItem.z = 0
            cushionItem.matrix = new Matrix4().setPosition(0, 0, 0)
            if (!parentItem.childs) parentItem.childs = new Set()
            parentItem.childs.add(fixedCushionId as any)
          })
        } catch (fixError) {
          console.warn('No se pudo corregir cojín automático:', fixError)
        }
      }

      // Ventana corta final por si los hijos se insertan justo al final del ciclo.
      await new Promise((resolve) => setTimeout(resolve, 120))
      const seatCount = dedupeSeatsForParent()
      const cushionCount = dedupeCushionsForParent()
      const seatExists = seatCount > 0 || parentHasSeatInChilds()
      const cushionExists = cushionCount > 0 || parentHasCushionInChilds()
      const cushionsNowForValidation = findCushionsForParent()
      const hasMatchingCushion = cushionsNowForValidation.some(([, child]) =>
        isMatchingCushionForTarget(child),
      )
      if (seatExists && cushionExists && hasMatchingCushion) {
        normalizeSeatsAndCushionsForParent()
        return
      }

      try {
        const drawLinkedSon = async (code: string) =>
          await get().drawItem(
            getId('linkedSon'),
            code,
            '',
            true,
            liveParent.matrix.clone(),
            'linkedSon',
            parentCatalog,
            false,
            { preserveSelection: true },
          )

        let seatId: AnyID | null = null
        if (!seatExists) {
          try {
            seatId = await drawLinkedSon(targetSeatCatalog)
          } catch {
            seatId = await drawLinkedSon(
              targetSeatCatalog.charAt(0).toUpperCase() + targetSeatCatalog.slice(1),
            )
          }
        }

        if (seatId) {
          set((state) => {
            const seatItem = state.items.get(seatId as AnyID) as any
            const parentItem = state.items.get(liveParent.id as AnyID) as any
            if (!seatItem || !parentItem) return

            seatItem.firstParentID = liveParent.id
            seatItem.depth = 1
            seatItem.canCollide = false
            seatItem.x = 0
            seatItem.y = 0
            seatItem.z = 0
            seatItem.matrix = new Matrix4().setPosition(seatItem.x, seatItem.y, seatItem.z)

            if (!parentItem.childs) parentItem.childs = new Set()
            parentItem.childs.add(seatId as any)
          })
        }

        // Intentar cojines con patrón dinámico por catalogID del padre.
        if (!cushionExists) {
          try {
            let cushionId: AnyID
            const requestedCushionCatalog = getTargetCushionCatalog()
            try {
              cushionId = await drawLinkedSon(requestedCushionCatalog)
            } catch {
              cushionId = await drawLinkedSon(
                requestedCushionCatalog.charAt(0).toUpperCase() + requestedCushionCatalog.slice(1),
              )
            }
            set((state) => {
              const cushionItem = state.items.get(cushionId as AnyID) as any
              const parentItem = state.items.get(liveParent.id as AnyID) as any
              if (!cushionItem || !parentItem) return
              cushionItem.firstParentID = liveParent.id
              cushionItem.depth = 1
              cushionItem.canCollide = false
              cushionItem.x = 0
              cushionItem.y = 0
              cushionItem.z = 0
              cushionItem.matrix = new Matrix4().setPosition(0, 0, 0)
              if (!parentItem.childs) parentItem.childs = new Set()
              parentItem.childs.add(cushionId as any)
            })
          } catch (error) {
            console.warn('No se pudo dibujar cojines automático:', error)
          }
        }

        await enforceMatchingCushionForParent()

        // Protección final contra duplicados por carreras asíncronas.
        dedupeSeatsForParent()
        dedupeCushionsForParent()
        normalizeSeatsAndCushionsForParent()
        // Segunda pasada tardía: fuera del batch de dibujo para no fragmentar undo/redo.
        setTimeout(() => {
          void runWithoutHistoryRecordingAsync(async () => {
            dedupeSeatsForParent()
            dedupeCushionsForParent()
            await enforceMatchingCushionForParent()
            normalizeSeatsAndCushionsForParent()
          })
        }, 1200)
      } catch (error) {
        console.warn('No se pudo dibujar asiento automático:', error)
      }
}

/**
 * Brazos (BR): heredan acabado/textura del linkedItem padre seleccionado.
 * Se aplica aquí para que funcione siempre, independientemente del panel/UI.
 */
const scheduleArmMaterialInheritance = (ctx: DrawItemPostProcessingContext) => {
  const { get, set, item, catalogID, sourceCurrentObjectID } = ctx
  if (
    /^BR/i.test(String(catalogID)) &&
    sourceCurrentObjectID &&
    sourceCurrentObjectID !== item.id
  ) {
    setTimeout(() => {
      const liveStore = get()
      const parentItem = liveStore.items.get(sourceCurrentObjectID as AnyID) as any
      const armItem = liveStore.items.get(item.id as AnyID) as any
      const editZoneFn = (liveStore as any).editZone

      if (!parentItem || !armItem || !isLinkedItem(parentItem) || !editZoneFn) return

      const parentTexture =
        parentItem.textures?.find(
          (t: any) =>
            t?.zoneDisplayName === 'Estructura' ||
            t?.zoneDisplayName === 'Armazon' ||
            t?.zoneDisplayName === 'ArmazonBlanco',
        ) || parentItem.textures?.[0]
      if (!parentTexture?.zoneName) return

      const parentMaterial = parentItem.zones?.[parentTexture.zoneName]?.material ?? {
        ...parentTexture,
      }

      const armZoneNamesFromZones = Object.keys(armItem?.zones || {})
      const armZoneNamesFromTextures = Array.isArray(armItem?.textures)
        ? armItem.textures.map((t: any) => t?.zoneName).filter(Boolean)
        : []
      const armZoneNames = Array.from(
        new Set([...armZoneNamesFromZones, ...armZoneNamesFromTextures]),
      )
      if (!armZoneNames.length) return

      // En frameloop=demand puede faltar un repaint final si coinciden
      // varias actualizaciones asíncronas seguidas. Forzamos un pulso
      // temporal en always para garantizar que el acabado del brazo
      // se pinte sin requerir interacción manual del usuario.
      const previousFrameloop = liveStore.frameloop
      set((s) => {
        s.frameloop = 'always'
      })

      armZoneNames.forEach((zoneName) => {
        editZoneFn(armItem.id, zoneName, { ...parentMaterial }, true)
      })

      // La herencia de material de brazo usa skipCounter=true para evitar
      // múltiples renders por zona. Forzamos un único repaint al final
      // para evitar que el brazo quede negro hasta interactuar con el canvas.
      set((s) => {
        s.contadorModificaciones = (s.contadorModificaciones || 0) + 1
      })
      try {
        const r3f = typeof window !== 'undefined' ? (window as any).r3f : undefined
        if (r3f?.invalidate) {
          r3f.invalidate()
          requestAnimationFrame(() => r3f.invalidate?.())
          setTimeout(() => r3f.invalidate?.(), 40)
          setTimeout(() => r3f.invalidate?.(), 120)
        }
      } catch {
        // noop
      }
      setTimeout(() => {
        set((s) => {
          s.frameloop = previousFrameloop
        })
      }, 700)
    }, 0)
  }
}

/**
 * Items universales: aplica la textura/material guardado (herencia) a los nuevos
 * items cuando ya existen otros items universales en la escena.
 */
const scheduleUniversalTextureInheritance = (ctx: DrawItemPostProcessingContext) => {
  const { get, item } = ctx
  // APLICAR TEXTURA GUARDADA A NUEVOS ITEMS (HERENCIA)
  if (item.type === 'items' && item.universal && item.textures && item.textures.length > 0) {
    const storeAfterCreate = get()
    const savedMaterial = (storeAfterCreate as any).selectedEstructuraMaterial

    // Verificar si hay items existentes en escena (excluyendo el item que se acaba de crear)
    const existingItems = Array.from(storeAfterCreate.items.values()).filter(
      (existingItem) =>
        existingItem.type === 'items' && existingItem.id !== item.id && existingItem.universal,
    )

    // Solo aplicar herencia si hay items existentes Y hay material guardado
    if (savedMaterial && existingItems.length > 0) {
      // ATEMPORAL: priorizar zona "Tela" (fallback "Estructura" para compatibilidad)
      const inheritedTexture =
        item.textures.find((t: any) => t.zoneDisplayName === 'Tela') ??
        item.textures.find((t: any) => t.zoneDisplayName === 'Estructura')

      if (inheritedTexture && item.zones[inheritedTexture.zoneName]) {
        // Aplicar el material guardado usando editZone
        setTimeout(() => {
          const storeForEdit = get()
          const editZoneFn = storeForEdit.editZone
          if (editZoneFn) {
            const finalMaterial = { ...savedMaterial }
            // Asegurar que la URL esté limpia
            if (finalMaterial.url) {
              finalMaterial.url = finalMaterial.url.split('?')[0]
            }
            editZoneFn(item.id, inheritedTexture.zoneName, finalMaterial, true)
          }
        }, 100)
      }
    }
  }
}

/**
 * Encuadre post-dibujo (`DRAW_FIT_CAMERA_AFTER_DRAW`):
 * - 1ª unidad: encuadre canónico de producto.
 * - 2ª unidad en adelante: no se solicita fit; se conserva el punto de vista actual.
 */
const scheduleCameraFit = (ctx: DrawItemPostProcessingContext) => {
  if (!DRAW_FIT_CAMERA_AFTER_DRAW) return

  const { get, catalogID, type, suppressCameraFit } = ctx
  const isArmCatalog = /^BR/i.test(String(catalogID || ''))
  const shouldRequestCameraFit =
    !suppressCameraFit && type !== 'linkedSon' && !isArmCatalog && !isPouff(catalogID)
  if (!shouldRequestCameraFit) return

  // Tras `drawItem` el item ya está en el store: si hay más de una unidad, no mover la cámara.
  const regularUnitCount = Array.from(get().items.values()).filter(
    (item) =>
      (item.type === 'items' || item.type === 'linkedItem') &&
      item.matrix &&
      item.width &&
      item.height,
  ).length
  if (regularUnitCount > 1) return

  const tryApplyDrawCameraFitOnce = (): boolean => {
    const store = get()
    if (store.cameraViewPreservationDepth > 0 || store.unitReplaceInFlight) return false
    if (
      store.mode === 'MODE_DRAGGING_ITEM_3D' ||
      store.mode === 'MODE_DRAGGING_ITEM' ||
      useModularUiStore.getState().itemDragActive
    ) {
      return false
    }

    const fittedView = applyLiveDrawCameraFit(store.items as Map<string, any>)
    if (!fittedView) return false

    store.setLastView({
      position: fittedView.position.clone(),
      target: fittedView.target.clone(),
    })
    store.clearCameraFitToItems()
    return true
  }

  window.requestAnimationFrame(() => {
    if (tryApplyDrawCameraFitOnce()) return
    const store = get()
    if (store.cameraViewPreservationDepth > 0 || store.unitReplaceInFlight) return
    store.requestCameraFitToItems()
  })
}

/**
 * linkedItem (unidad): reancla los brazos exteriores tras dibujar una unidad
 * para que mantengan su posición relativa correcta.
 */
const scheduleExteriorArmRealign = (ctx: DrawItemPostProcessingContext) => {
  const { get, item, catalogID, type, suppressExteriorArmRealign } = ctx
  const isArmCatalog = /^BR/i.test(String(catalogID || ''))
  // Igual que RELAX: tras dibujar Corner/oreja/rinconero, realignExteriorArms
  // purga el BR del labio ocupado (newUnitHostsArms=false). No suprimir ese paso.
  const shouldRealignExteriorArms =
    !suppressExteriorArmRealign &&
    !item.universal &&
    item.type === 'linkedItem' &&
    type !== 'linkedSon' &&
    !isArmCatalog &&
    !isPouff(catalogID)

  if (shouldRealignExteriorArms) {
    setTimeout(() => {
      void realignExteriorArmsAfterUnitDraw(item.id as AnyID, {
        getItem: (id) => get().items.get(id) as any,
        listItems: () => get().items.values() as Iterable<any>,
        editItem: (id, data) => {
          get().editItem(id, data as any)
        },
        collideItems: (itemId, otherId, options) => {
          get().collideItems(itemId, otherId, options)
        },
        replaceItem: (id, nextCatalog, icon, itemType) =>
          get().replaceItem(id, nextCatalog, icon, itemType),
        removeItem: (id) => {
          // Misma purga robusta que Toolbar/RELAX (resuelve clave Map + childs).
          get().removeSceneItemsByIds([id])
        },
      }).catch((error) => {
        console.warn('[drawItem] No se pudieron reanclar brazos exteriores:', error)
      })
    }, 0)
  }
}

/**
 * linkedItem: precarga las URLs de textura del item y, cuando hereda texturas,
 * empuja los materiales de zona al renderer.
 */
const preloadDrawnItemTextures = async (ctx: DrawItemPostProcessingContext) => {
  const { get, item, options } = ctx
  if (options?.inheritTexturesFrom?.textures?.length && !item.universal) {
    await preloadSceneTextureUrls(item.textures.map((texture: { url?: string }) => texture.url))
    pushLinkedItemZoneMaterialsToRenderer(get, item.id as AnyID)
  } else if (item.textures?.length && !item.universal) {
    await preloadSceneTextureUrls(item.textures.map((texture: { url?: string }) => texture.url))
  }
}

/**
 * Orquesta el comportamiento post-dibujo específico de cada tipo de item una vez
 * que ya está colocado en la escena. Mantiene el orden original de `drawItem`.
 */
export const runDrawItemPostProcessing = async (ctx: DrawItemPostProcessingContext) => {
  reapplyParentSnapAfterDraw(ctx)
  scheduleCameraFit(ctx)
  await runAutoSeatAndCushion(ctx)
  scheduleArmMaterialInheritance(ctx)
  scheduleUniversalTextureInheritance(ctx)
  scheduleExteriorArmRealign(ctx)
  await preloadDrawnItemTextures(ctx)
}
